import { Component, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Table, TableLazyLoadEvent, TableModule } from "@openng/optimus-ui/table";
import { GateFaultService } from "../../services/api/gate-fault/gate-fault.service";
import { AssetHierarchyComponent } from "../../shared/widgets/asset-hierarchy/asset-hierarchy.component";
import { first } from "rxjs";
import { FormsModule } from "@angular/forms";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { DateTime } from "luxon";
import { NgClass } from "@angular/common";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { GateIdentifierPipe } from "../../shared/pipes/gateIdentifier/gate-identifier.pipe";
import { PAGINATOR_CONSTANTS } from "../../constants/paginator";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { Column } from "../../models/ui/tableColumn";
import { FaultReportData } from "../../models/api/fault/faultReportData";
import { ASSET_TYPES, AssetType } from "../../enums/assetTypes.enum";
import { Sort } from "../../models/api/sort";
import { CursorPagination } from "../../models/api/pagination";

@Component({
  standalone: true,
  selector: "app-fault-reporting",
  imports: [
    TranslateModule,
    TableModule,
    AssetHierarchyComponent,
    FormsModule,
    SvgIconComponent,
    NgClass,
    TooltipModule,
    HasPermissionDirective,
    TimeoutComponent,
    ProgressSpinnerModule,
    GateIdentifierPipe,
  ],
  templateUrl: "./fault-reporting.component.html",
  styleUrl: "./fault-reporting.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class FaultReportingComponent implements OnInit {
  public paginatorOptions = PAGINATOR_CONSTANTS;
  public translate = inject(TranslateService);
  public permissions = inject(PermissionService);
  public loading = signal(true);
  public timeOut = createTimeoutSignal(this.loading);
  public filteredFaultReportingData = signal<(FaultReportData | undefined)[]>([]);
  public totalFaultRecords = 0;
  public COLUMNS: Column[] = [
    {
      field: "faultCode",
      header: this.translate.instant("fault_reporting.table.fault_code"),
    },
    {
      field: "gateSystem",
      header: this.translate.instant("fault_reporting.table.gate_system"),
    },
    {
      field: "faultType",
      header: this.translate.instant("fault_reporting.table.fault_type"),
    },
    {
      field: "triggeredAt",
      header: this.translate.instant("fault_reporting.table.triggered_at"),
    },
    {
      field: "clearedAt",
      header: this.translate.instant("fault_reporting.table.cleared_at"),
    },
    {
      field: "control",
      header: this.translate.instant("fault_reporting.table.control"),
    },
    {
      field: "faultText",
      header: this.translate.instant("fault_reporting.table.fault_text"),
    },
  ];
  @ViewChild(Table) table: Table | undefined;
  currSort: { column: string; order: number } = {
    column: "triggeredAt",
    order: -1,
  };
  private gateFaultService = inject(GateFaultService);
  private initialLoadComplete = false;
  #assetType: AssetType | undefined;
  #assetId: string | undefined;
  #sort: Sort = { column: "triggeredAt", descending: true };
  #pagination: CursorPagination = {
    pageSize: this.paginatorOptions.DEFAULT_ROWS,
  };

  ngOnInit(): void {
    this.loadNewFaultReportingData();
  }

  /**
   * Loads new fault reporting data for and updates the relevant data structures and UI components.
   * "New" meaning if sorting or asset selection changed
   * @return {void} No return value.
   */
  public loadNewFaultReportingData(): void {
    this.initialLoadComplete = false;
    delete this.#pagination.cursor;

    this.gateFaultService
      .getFaultReportingData(this.#assetId, this.#assetType, undefined, this.#pagination, this.#sort)
      .pipe(first())
      .subscribe({
        next: (faultReportingData) => {
          this.totalFaultRecords = faultReportingData.total;
          const newData = Array.from<FaultReportData>({
            length: this.totalFaultRecords,
          });
          newData.splice(0, this.#pagination.pageSize, ...faultReportingData.items);
          this.filteredFaultReportingData.set(newData);

          if (this.table && this.table.scroller) {
            this.table.scroller.lazyLoadState = {
              first: 0,
              last: this.#pagination.pageSize,
            };

            this.table._sortField = this.currSort.column;
            this.table._sortOrder = this.currSort.order ?? -1;
          }
          this.initialLoadComplete = true;
          this.loading.set(false);
        },
        error: () => {
          this.filteredFaultReportingData.set([]);
        },
      });
  }

  /**
   * Handles lazy loading table trigger. Loads more data if sorting or selection hasn't changed,
   * otherwise new data is loaded.
   * @param {TableLazyLoadEvent} event
   * @return {void} No return value.
   */
  public loadMoreGateFaults(event: TableLazyLoadEvent) {
    if (!this.initialLoadComplete || this.loading()) {
      return;
    }
    if (
      typeof event.sortField === "string" &&
      !!event.sortOrder &&
      (event.sortField !== this.currSort.column || event.sortOrder !== this.currSort.order)
    ) {
      this.handleSorting(event);
      return;
    }

    const alarmCursor = this.filteredFaultReportingData()?.[(event.first ?? 1) - 1];
    if (alarmCursor && typeof event.sortField === "string") {
      const otherCursor =
        event.sortField !== "gateSystem"
          ? alarmCursor[event.sortField as keyof FaultReportData]
          : alarmCursor.gateSystem.label;
      this.#pagination.cursor = `${alarmCursor.id}__${otherCursor}`;
    }

    this.gateFaultService
      .getFaultReportingData(this.#assetId, this.#assetType, undefined, this.#pagination, this.#sort)
      .pipe(first())
      .subscribe({
        next: (faultReportingData) => {
          const newGateFaults = faultReportingData.items;
          this.filteredFaultReportingData.update((data) => {
            data.splice(event.first ?? 0, newGateFaults.length, ...newGateFaults);
            return data;
          });
        },
        error: (_) => {
          this.filteredFaultReportingData.set([]);
        },
      });
  }

  /**
   * Handle sorting change and if changed trigger event to load new data.
   * @param {TableLazyLoadEvent} event
   * @return void
   */
  public handleSorting(event: TableLazyLoadEvent) {
    let sortColumn: string;
    switch (event.sortField) {
      case "faultCode":
      case "faultText":
      case "faultType":
      case "clearedAt":
      case "triggeredAt":
        sortColumn = event.sortField;
        break;
      case "gateSystem":
        sortColumn = "gateSystem.label";
        break;
      case "control":
        sortColumn = "gateSystem.controller";
        break;
      default:
        sortColumn = "triggeredAt";
        break;
    }
    const sortDescending = (event.sortOrder ?? -1) < 0;
    if (sortColumn !== this.#sort.column || sortDescending !== this.#sort.descending) {
      this.#sort.column = sortColumn;
      this.#sort.descending = sortDescending;
      this.currSort = {
        column: event.sortField as string,
        order: event.sortOrder ?? -1,
      };
      this.loadNewFaultReportingData();
    }
  }

  /**
   * Handles asset click event.
   * Requests fault reporting data for the selected asset
   * and updates the table with the received data.
   * @param asset asset object with id and assetType properties
   */
  public onAssetClicked(asset: { id: string; assetType: string }) {
    this.loading.set(true);
    this.#assetId = asset.id;
    this.#assetType = asset.assetType as ASSET_TYPES;
    this.#sort = { column: "triggeredAt", descending: true };
    this.#pagination = { pageSize: this.paginatorOptions.DEFAULT_ROWS };
    this.currSort = { column: "triggeredAt", order: -1 };

    this.loadNewFaultReportingData();
  }

  /**
   * Tell Angulars shadow DOM to track by gateFaultId, which is unique in this dataset
   * @param _
   * @param {FaultReportData} gateFault
   */
  public trackByAlarmId(_: number, gateFault: FaultReportData) {
    return gateFault.id;
  }

  /**
   * Converts a timestamp in milliseconds to a formatted date string.
   * @param time - The timestamp in milliseconds.
   * @returns A string formated
   */
  public convertTime(time: number) {
    return DateTime.fromMillis(time).toFormat("dd.MM.yyyy - HH:mm:ss");
  }

  private getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const match = contentDisposition.match(/filename="([^"]+)"/i);
    return match?.[1] ?? null;
  }

  /**
   * Requests export of fault reporting data as CSV for the currently selected asset
   */
  public onExportClick() {
    this.gateFaultService.getGateFaultExport(this.#assetId, this.#assetType).subscribe({
      next: (res) => {
        if (res && res.body) {
          const blob = new Blob([res.body], {
            type: "text/csv;charset=utf-8",
          });
          const link = document.createElement("a");

          const contentDisposition = res.headers.get("content-disposition");
          const filename = this.getFilenameFromContentDisposition(contentDisposition) ?? "gate_faults_export.csv";

          link.download = filename;
          link.href = window.URL.createObjectURL(blob);
          link.style.display = "none";
          document.body.appendChild(link);

          link.click();
          document.body.removeChild(link);
        }
      },
    });
  }
}
